import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from './Header';
import Footer from './Footer';

const OrlandoDateNightPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const toggleFaq = (index) => setOpenFaq(openFaq === index ? null : index);

  const faqs = [
    {
      question: "Why is a magic show perfect for date night?",
      answer: "A magic show creates shared moments of wonder and amazement that bring couples closer together. Unlike movies where you sit in silence, Mental Hop Scotch is interactive and gives you plenty to talk about during and after the show. It's a unique, memorable experience you'll both cherish."
    },
    {
      question: "Is the show romantic?",
      answer: "While not traditionally romantic, the intimate setting and sense of wonder creates a magical atmosphere perfect for couples. The shared experience of witnessing impossible feats together creates lasting memories and great conversation."
    },
    {
      question: "What should we wear for date night at the show?",
      answer: "Smart casual is perfect! Many couples dress up a bit for the evening, but there's no strict dress code. Wear something comfortable that makes you feel good."
    },
    {
      question: "Are there restaurants nearby for dinner?",
      answer: "Yes! The Orlando area has many excellent restaurants nearby. We recommend making dinner reservations before or after the 7 PM show to complete your perfect date night."
    },
    {
      question: "Can we get couples seating?",
      answer: "Absolutely! When booking tickets, you can select adjacent seats. VIP Experience seats in the front rows offer the most intimate viewing experience for couples."
    },
    {
      question: "How long is the show?",
      answer: "The show runs 90 minutes with a 15-minute intermission - perfect for a date night that's exciting but not too late."
    }
  ];

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "Orlando Date Night - Mental Hop Scotch Magic Show",
    "description": "The perfect Orlando date night experience. Mental Hop Scotch is an interactive magic show that creates unforgettable shared moments for couples.",
    "image": "https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/nyl6m05g_mental2.jpg",
    "url": "https://www.simonnomislive.com/orlando-date-night",
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "performer": { "@type": "Person", "name": "Simon Nomis" },
    "location": {
      "@type": "Place",
      "name": "Orlando, FL",
      "address": { "@type": "PostalAddress", "addressLocality": "Orlando", "addressRegion": "FL", "addressCountry": "US" }
    },
    "offers": { "@type": "Offer", "price": "35", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "url": "https://simonnomismagcentertainment.ticketspice.com/mental-hop-scotch" }
  };

  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>Orlando Date Night Ideas | Magic Show for Couples | Simon Nomis Live</title>
        <meta name="description" content="Looking for unique Orlando date night ideas? Experience Mental Hop Scotch - an interactive magic show perfect for couples. Create unforgettable memories together!" />
        <meta name="keywords" content="Orlando date night, date night Orlando, romantic things to do Orlando, couples activities Orlando, Orlando date ideas, unique date night, magic show date" />
        <link rel="canonical" href="https://www.simonnomislive.com/orlando-date-night" />
        <meta property="og:title" content="Orlando Date Night Ideas | Magic Show for Couples" />
        <meta property="og:description" content="Looking for unique Orlando date night ideas? Experience Mental Hop Scotch - an interactive magic show perfect for couples!" />
        <meta property="og:url" content="https://www.simonnomislive.com/orlando-date-night" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/nyl6m05g_mental2.jpg" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-black via-purple-900/30 to-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              <span className="text-purple-400">Orlando</span> Date Night
            </h1>
            <p className="text-xl md:text-2xl text-purple-300 mb-8">Create Unforgettable Memories Together</p>
            <p className="text-lg text-gray-300 mb-10 leading-relaxed">
              Looking for the perfect date night in Orlando? Skip the ordinary dinner-and-a-movie routine and experience something truly magical together. <strong className="text-purple-400">Mental Hop Scotch</strong> is an interactive magic show that will have you and your partner gasping in amazement and laughing together.
            </p>
            <a href="https://simonnomismagcentertainment.ticketspice.com/mental-hop-scotch" target="_blank" rel="noreferrer" className="inline-block font-bold text-xl text-white rounded-full px-10 py-4 transition-all duration-300 hover:scale-105" style={{ backgroundColor: 'rgb(181, 36, 189)' }}>Book Your Date Night</a>
            <div className="mt-5 flex justify-center gap-1">
              <img alt="Visa" width="40" src="https://s3-us-west-2.amazonaws.com/assets.webconnex.com/images/cc-logos/cc-logo-visa.png" />
              <img alt="Mastercard" width="40" src="https://s3-us-west-2.amazonaws.com/assets.webconnex.com/images/cc-logos/cc-logo-mc.png" />
              <img alt="Amex" width="40" src="https://s3-us-west-2.amazonaws.com/assets.webconnex.com/images/cc-logos/cc-logo-amex.png" />
              <img alt="Discover" width="40" src="https://s3-us-west-2.amazonaws.com/assets.webconnex.com/images/cc-logos/cc-logo-discover.png" />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 text-center"><span className="text-purple-400">Why Couples Love</span> Mental Hop Scotch</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-purple-900/20 p-6 rounded-lg border border-purple-500/30 text-center">
                <div className="text-4xl mb-4">💑</div>
                <h3 className="text-xl font-bold text-purple-400 mb-3">Shared Wonder</h3>
                <p className="text-gray-300">Experience jaw-dropping moments together that you'll be talking about for weeks.</p>
              </div>
              <div className="bg-purple-900/20 p-6 rounded-lg border border-purple-500/30 text-center">
                <div className="text-4xl mb-4">🎭</div>
                <h3 className="text-xl font-bold text-purple-400 mb-3">Interactive Fun</h3>
                <p className="text-gray-300">Unlike passive entertainment, you both become part of the show's magic.</p>
              </div>
              <div className="bg-purple-900/20 p-6 rounded-lg border border-purple-500/30 text-center">
                <div className="text-4xl mb-4">✨</div>
                <h3 className="text-xl font-bold text-purple-400 mb-3">Unique Memory</h3>
                <p className="text-gray-300">Stand out from typical date nights with an experience neither of you will forget.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Show Details */}
      <section className="py-16 bg-gradient-to-br from-purple-900/20 via-black to-purple-900/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <img src="https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/nyl6m05g_mental2.jpg" alt="Mental Hop Scotch Date Night" className="rounded-lg shadow-2xl" />
              <div>
                <h2 className="text-3xl font-bold text-white mb-4">The <span className="text-purple-400">Perfect</span> Evening</h2>
                <div className="space-y-4 text-gray-300">
                  <p>📅 <strong className="text-white">When:</strong> Saturdays at 7:00 PM</p>
                  <p>📍 <strong className="text-white">Where:</strong> Orlando, FL</p>
                  <p>⏱️ <strong className="text-white">Duration:</strong> 90 minutes + intermission</p>
                  <p>💵 <strong className="text-white">Tickets:</strong> $35 - $70 per person</p>
                </div>
                <a href="https://simonnomismagcentertainment.ticketspice.com/mental-hop-scotch" target="_blank" rel="noreferrer" className="inline-block mt-6 font-bold text-lg text-white rounded-full px-8 py-3 transition-all duration-300 hover:scale-105" style={{ backgroundColor: 'rgb(181, 36, 189)' }}>Get Tickets for Two →</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 text-center"><span className="text-purple-400">Date Night</span> FAQs</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-900/50 rounded-lg border border-purple-500/30 overflow-hidden">
                  <button onClick={() => toggleFaq(index)} className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-purple-900/20 transition-colors">
                    <span className="text-white font-semibold pr-4">{faq.question}</span>
                    <span className="text-purple-400 text-2xl flex-shrink-0">{openFaq === index ? '−' : '+'}</span>
                  </button>
                  {openFaq === index && <div className="px-6 pb-4"><p className="text-gray-300 leading-relaxed">{faq.answer}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-purple-900/30 via-black to-purple-900/30">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Make Your Next Date Night <span className="text-purple-400">Magical</span></h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">Book now and give your partner an unforgettable experience!</p>
          <a href="https://simonnomismagcentertainment.ticketspice.com/mental-hop-scotch" target="_blank" rel="noreferrer" className="inline-block font-bold text-xl text-white rounded-full px-12 py-5 transition-all duration-300 hover:scale-105" style={{ backgroundColor: 'rgb(181, 36, 189)' }}>Book Date Night Tickets</a>
          <p className="text-gray-400 mt-6 text-sm">📍 Orlando, FL | Saturdays at 7PM</p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OrlandoDateNightPage;